import { colors } from '@theme/foundations/colors';
import { DEFAULT_THRESHOLD } from '@utils/map/map.constants';
import { SHOW_STATISTIC_POINT_MIN_ZOOM } from '@utils/map/mapZoom.constants';
import {
  CircleLayerSpecification,
  DataDrivenPropertyValueSpecification,
  LineLayerSpecification,
  SymbolLayerSpecification,
} from 'mapbox-gl';

import { DroneAlertLevelEnum } from '@/types/data/data.types';
import { FeatureTypeEnum, LayerNameEnum, SourceNameEnum, StatisticFilterColorMode } from '@/types/map.types';

export const trajectoryOutlineLayer: LineLayerSpecification = {
  id: LayerNameEnum.STATISTICS_TRAJECTORIES_OUTLINES,
  type: 'line',
  source: SourceNameEnum.STATISTICS,
  filter: ['==', 'featureType', FeatureTypeEnum.STATISTIC_TRAJECTORY],
  paint: {
    'line-color': 'black',
    'line-width': 6,
  },
};

const colorByMode = (
  threshold: number,
): Record<StatisticFilterColorMode, DataDrivenPropertyValueSpecification<string> | undefined> => ({
  byMaxAlertLevel: [
    'match',
    ['get', 'worstAlertLevel'],
    DroneAlertLevelEnum.DRONE_CRITICAL,
    colors.red.DEFAULT.value,
    DroneAlertLevelEnum.DRONE_WARNING,
    colors.orange.DEFAULT.value,
    DroneAlertLevelEnum.DRONE_INFO,
    colors.yellow.DEFAULT.value,
    colors.neutral[500].value,
  ],

  byMaxHeight: [
    'case',
    ['<', ['coalesce', ['get', 'maxHeight'], DEFAULT_THRESHOLD], threshold],
    colors.green.DEFAULT.value,
    colors.red.DEFAULT.value,
  ],

  byTrajectory: ['get', 'trajectoryColor'],
});

export const statisticLayer = (
  colorMode: StatisticFilterColorMode,
  threshold: number = DEFAULT_THRESHOLD,
): CircleLayerSpecification => ({
  id: LayerNameEnum.STATISTICS_POINTS,
  type: 'circle',
  source: SourceNameEnum.STATISTICS,
  filter: ['==', 'featureType', FeatureTypeEnum.STATISTIC_POINT],
  minzoom: SHOW_STATISTIC_POINT_MIN_ZOOM,
  paint: {
    'circle-radius': 4,
    'circle-color': colorByMode(threshold)[colorMode] ?? colors.neutral[500].value,
    'circle-stroke-width': 2,
    'circle-stroke-color': 'black',
  },
});

export const trajectoryLayer = (
  colorMode: StatisticFilterColorMode,
  threshold: number = DEFAULT_THRESHOLD,
): LineLayerSpecification => ({
  id: LayerNameEnum.STATISTICS_TRAJECTORIES,
  type: 'line',
  source: SourceNameEnum.STATISTICS,
  filter: ['==', 'featureType', FeatureTypeEnum.STATISTIC_TRAJECTORY],
  layout: {
    'line-join': 'round',
    'line-cap': 'round',
  },
  paint: {
    'line-color': colorByMode(threshold)[colorMode] ?? colors.neutral[500].value,
    'line-width': 2,
  },
});

export const trajectoryArrowLayer = (
  colorMode: StatisticFilterColorMode,
  threshold: number = DEFAULT_THRESHOLD,
): SymbolLayerSpecification => ({
  id: LayerNameEnum.STATISTIC_TRAJECTORY_ARROWS,
  type: 'symbol',
  source: SourceNameEnum.STATISTICS,
  minzoom: SHOW_STATISTIC_POINT_MIN_ZOOM,
  filter: ['==', 'featureType', FeatureTypeEnum.STATISTIC_TRAJECTORY],
  layout: {
    'symbol-placement': 'line',
    'symbol-spacing': 400,
    'icon-image': 'trajectory-arrow',
    'icon-size': 0.65,
    'icon-allow-overlap': true,
    'icon-ignore-placement': true,
  },
  paint: {
    'icon-color': colorByMode(threshold)[colorMode] ?? colors.neutral[500].value,
  },
});

export const trajectoryArrowOutlineLayer: SymbolLayerSpecification = {
  id: LayerNameEnum.STATISTIC_TRAJECTORY_ARROWS_OUTLINES,
  type: 'symbol',
  source: SourceNameEnum.STATISTICS,
  minzoom: SHOW_STATISTIC_POINT_MIN_ZOOM,
  filter: ['==', 'featureType', FeatureTypeEnum.STATISTIC_TRAJECTORY],
  layout: {
    'symbol-placement': 'line',
    'symbol-spacing': 400,
    'icon-image': 'trajectory-arrow-outline',
    'icon-size': 0.6,
    'icon-allow-overlap': true,
    'icon-ignore-placement': true,
  },
};

type StatisticLayerStyles = {
  trajectory: LineLayerSpecification;
  trajectoryArrow: SymbolLayerSpecification;
  statistic: CircleLayerSpecification;
};

const layerStylesCache = new Map<string, StatisticLayerStyles>();

export const getStatisticLayerStyles = (
  colorMode: StatisticFilterColorMode,
  threshold: number = DEFAULT_THRESHOLD,
): StatisticLayerStyles => {
  const cacheKey = `${colorMode}:${threshold}`;
  const cached = layerStylesCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const computedStyles: StatisticLayerStyles = {
    trajectory: trajectoryLayer(colorMode, threshold),
    trajectoryArrow: trajectoryArrowLayer(colorMode, threshold),
    statistic: statisticLayer(colorMode, threshold),
  };
  layerStylesCache.set(cacheKey, computedStyles);

  return computedStyles;
};
